/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Subject } from "../../../Objects/models/Subject";
import { store } from "../../../Redux/store";
import { getSubject } from "../../../Redux/Actions/PunctualCourseActions";
import { getActiveBrandConfig } from "../../../config/environment.config";
import { vocab } from "../vocabulary";
import { FieldError, FieldLabel, Hint, TextLink, fieldStyle, useDv2Theme } from "../layout/Ui";

interface Props {
    teachrId: number | undefined;
    value: Subject | null;
    onChange: (subject: Subject | null) => void;
    error?: string;
}

/**
 * Matière (ou prestation) de l'étape 3 : les matières du profil du prof,
 * classées par ordre alphabétique (l'audit relevait une liste non triée et
 * une sélection ratée pendant le chargement). Une matière absente s'ajoute
 * depuis le profil.
 */
const SubjectSelect = ({ teachrId, value, onChange, error }: Props) => {
    const { fontFamily } = useDv2Theme();
    const navigate = useNavigate();
    const given: Subject[] | null = useSelector((s: any) => s?.punctualCourse?.subject);

    useEffect(() => {
        if (teachrId) store.dispatch(getSubject(teachrId) as any);
    }, [teachrId]);

    const options = useMemo(() => {
        const list = Array.isArray(given) ? [...given] : [];
        return list.sort((a, b) => (a.fr_FR ?? "").localeCompare(b.fr_FR ?? "", "fr", { sensitivity: "base" }));
    }, [given]);
    const loading = given == null;
    const label = vocab.subjectLabel();

    return (
        <div>
            <FieldLabel htmlFor="dv2-subject">{label}</FieldLabel>
            <select
                id="dv2-subject"
                value={value?.id ?? ""}
                onChange={(e) => onChange(options.find((s) => String(s.id) === e.target.value) ?? null)}
                aria-invalid={!!error}
                aria-describedby={error ? "dv2-subject-error" : undefined}
                disabled={loading}
                style={{ ...fieldStyle(!!error), fontFamily, appearance: "auto" as any }}
            >
                <option value="" disabled>{loading ? "Chargement…" : `Choisir ${vocab.subjectLabelLower() === "matière" ? "une matière" : "une prestation"}`}</option>
                {options.map((subject) => <option key={subject.id} value={subject.id}>{subject.fr_FR}</option>)}
            </select>
            {error ? <FieldError id="dv2-subject-error">{error}</FieldError> : (
                <Hint>
                    Liste classée par ordre alphabétique.{" "}
                    {!loading && options.length === 0
                        ? <>Aucune {vocab.subjectLabelLower()} dans votre profil : </>
                        : <>Une {vocab.subjectLabelLower()} manque ? </>}
                    <TextLink label="Compléter mon profil" size={12} onClick={() => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, { state: { step: 1, prestation: true } })} />
                </Hint>
            )}
        </div>
    );
};

export default SubjectSelect;
